import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Mail, ArrowLeft } from 'lucide-react';
import { apiClient } from '../services/api';
import Footer from './Footer';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            await apiClient.forgotPassword(email.toLowerCase());
            setSubmitted(true);
        } catch (err: any) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-white flex flex-col">
            <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-10" style={{ boxShadow: '0 20px 60px -12px rgba(0, 0, 0, 0.15), 0 8px 16px -8px rgba(0, 0, 0, 0.1)' }}>
                    <div className="text-center mb-8">
                        <img
                            src="/logo-horizontal-black.png"
                            alt="IntelliDrive"
                            className="mx-auto h-32 w-auto mb-3"
                        />
                        <p className="text-sm text-gray-600">Intelligent Driver Compliance Platform</p>
                    </div>

                    {submitted ? (
                        <div>
                            <div className="flex flex-col items-center text-center mb-6">
                                <CheckCircle className="h-12 w-12 mb-3" style={{ color: '#69BE28' }} />
                                <h2 className="text-xl font-semibold text-gray-900 mb-2">Check your email</h2>
                                <p className="text-sm text-gray-600">
                                    If <strong>{email}</strong> is registered on IntelliDrive, you'll receive a password reset link shortly. The link expires in 1 hour.
                                </p>
                            </div>
                            <p className="text-xs text-gray-500 text-center mb-6">
                                Didn't receive anything? Check your spam folder, or make sure you entered the correct email address.
                            </p>
                            <Link
                                to="/login"
                                className="flex items-center justify-center gap-2 w-full py-3 px-4 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back to sign in
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-1">Forgot your password?</h2>
                                <p className="text-sm text-gray-500">Enter your email address and we'll send you a reset link.</p>
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                    Email address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Mail className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        className="appearance-none block w-full pl-10 pr-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.3)' } as React.CSSProperties}
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={e => {
                                            setEmail(e.target.value);
                                            if (error) setError(null);
                                        }}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                    <span className="text-sm text-red-800">{error}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading || !email}
                                className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                style={{
                                    backgroundColor: '#69BE28',
                                    '--tw-ring-color': 'rgba(105, 190, 40, 0.5)'
                                } as React.CSSProperties}
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                        Sending...
                                    </div>
                                ) : (
                                    'Send reset link'
                                )}
                            </button>

                            <Link
                                to="/login"
                                className="flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back to sign in
                            </Link>
                        </form>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}
